import { useEffect, useMemo } from 'react';
import { type FieldError, useForm, useWatch } from 'react-hook-form';

import { useIntl } from '../../hooks';
import { zodResolver } from '../../lib/zod-resolver.js';
import { createUserFormSchema, type UserFormValues } from '../../validations/user-form.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '../ui';
import { UserGrantsField } from './UserGrantsField.js';

type Mode = 'create' | 'edit';

type UserFormDialogProps = {
  open: boolean;
  mode: Mode;
  initialValues: UserFormValues;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: UserFormValues) => Promise<void | string>;
};

export default function AddUserDialog({
  open,
  mode,
  initialValues,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: UserFormDialogProps) {
  const intl = useIntl();
  const formSchema = useMemo(() => createUserFormSchema(mode), [mode]);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    control,
    formState: { errors, isValid },
  } = useForm<UserFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      reset(initialValues);
      clearErrors();
    }
  }, [open, initialValues, reset, clearErrors]);

  const onFormSubmit = handleSubmit(async (values) => {
    clearErrors('root');
    const result = await onSubmit({
      username: values.username.trim(),
      password: values.password,
      isAdmin: values.isAdmin,
      grants: values.grants,
    });

    if (typeof result === 'string' && result.length) {
      setError('root', {
        type: 'server',
        message: result,
      });
    }
  });

  const isAdmin = useWatch({ control, name: 'isAdmin' });

  const grantsError = errors.grants as FieldError | undefined;
  const grantsErrorMessage =
    typeof grantsError?.message === 'string' ? grantsError.message : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({
              id: mode === 'create' ? 'userDialog.title.add' : 'userDialog.title.edit',
            })}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onFormSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="username">
              {intl.formatMessage({ id: 'userDialog.field.username' })}
            </Label>
            <Input
              id="username"
              autoComplete="off"
              placeholder={intl.formatMessage({ id: 'userDialog.field.usernamePlaceholder' })}
              {...register('username')}
            />
            <p className="text-xs text-foreground">
              {intl.formatMessage({ id: 'userDialog.field.usernameHint' })}
            </p>
            {errors.username && (
              <p className="text-sm text-destructive">{errors.username.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">
              {intl.formatMessage({
                id:
                  mode === 'create'
                    ? 'userDialog.field.temporaryPassword'
                    : 'userDialog.field.resetPassword',
              })}
            </Label>
            <Input
              id="password"
              type="password"
              placeholder={intl.formatMessage({
                id:
                  mode === 'create'
                    ? 'userDialog.field.passwordPlaceholder'
                    : 'userDialog.field.passwordPlaceholderEdit',
              })}
              {...register('password')}
            />
            {mode === 'edit' && (
              <p className="text-xs text-foreground">
                {intl.formatMessage({ id: 'userDialog.field.passwordHintEdit' })}
              </p>
            )}
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>
          <div className="grid gap-2">
            <label
              htmlFor="isAdmin"
              className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
            >
              <input
                id="isAdmin"
                type="checkbox"
                className="h-4 w-4 rounded border-border accent-primary"
                data-testid="user-is-admin"
                {...register('isAdmin')}
              />
              {intl.formatMessage({ id: 'userDialog.field.administrator' })}
            </label>
            <p className="text-xs text-foreground">
              {intl.formatMessage({ id: 'userDialog.field.administratorHint' })}
            </p>
          </div>
          {!isAdmin && (
            <div className="grid gap-2">
              <UserGrantsField control={control} disabled={isSubmitting} />
              {grantsErrorMessage ? (
                <p className="text-sm text-destructive">{grantsErrorMessage}</p>
              ) : null}
            </div>
          )}
          {errors.root && <div className="text-sm text-destructive">{errors.root.message}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {intl.formatMessage({ id: 'userDialog.action.cancel' })}
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting
                ? intl.formatMessage({
                    id:
                      mode === 'create' ? 'userDialog.action.creating' : 'userDialog.action.saving',
                  })
                : intl.formatMessage({
                    id: mode === 'create' ? 'userDialog.action.create' : 'userDialog.action.save',
                  })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export type { UserFormValues };
